import { useState } from 'react';
import {
    Box,
    Typography,
    TextField,
    InputAdornment,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Link,
} from '@mui/material';
import { Search as SearchIcon, Add as AddIcon } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';

const linkSx = {
    color: '#1976d2',
    textDecorationThickness: '1px',
    '&:hover': {
        color: '#125aa0',
    },
};


// Sample data for the FAQs
const sampleFaqs = [
    {
        question: 'What measures are in place to ensure the security of my data?',
        answer: 'Your data\'s security is foundational to our service. Prospo AI is hosted exclusively with a leading Australian Tier 1 cloud provider, ensuring all data is stored within Australia. We further protect access with mandatory two-factor authentication (2FA).',
    },
    {
        question: 'How can I get support?',
        answer: 'We offer two convenient support channels: email and scheduled phone callbacks. To get help, please open a ticket by sending us an email. Our global support team, with members in Australia and India, is dedicated to providing you with a timely and effective response during your business hours.',
    },
    {
        question: 'How can I submit feature enhancement requests?',
        answer: 'We have a transparent, community-driven process for feature requests. Our team will guide you on how to submit your suggestions directly through our GitHub page. Once submitted, the wider user community can vote on proposals, which helps us prioritize the most sought-after enhancements for development.',
    },
    {
        question: 'Can I integrate Prospo with my CRM?',
        answer: (
            <>
                Yes, absolutely. Prospo offers direct integrations with{' '}
                <Link href="https://monday.com" sx={linkSx}>
                    Monday.com
                </Link>
                {' '}and the popular Worksorted CRM. Furthermore, because our platform is built on a flexible API-first architecture, we can work with any CRM vendor that provides an open API. We're eager to expand our connectivity to meet your needs.
            </>
        ),
    },
    {
        question: 'Do you have a free trial?',
        answer: 'Yes, we offer an extensive free trial designed to let you experience Prospo\'s full capabilities. You can onboard your first 20 clients and use the platform free of charge for 3 months. After the trial, our billing is a simple $50 per client upload, per year.',
    },
    {
        question: 'Can I download all my transcripts, summary notes, and filenotes if I wish to discontinue with Prospo?',
        answer: 'Yes, you can download all of your data—including transcripts, summaries, and file notes—at any time. We have a no lock-in policy, so you\'re free to take your information with you if you choose to discontinue. We will also delete it from our systems after 14 days.',
    },
];

const FAQComponent = () => {
    const [searchQuery, setSearchQuery] = useState('');
    const [expanded, setExpanded] = useState<string | false>(false);

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const filteredFaqs = sampleFaqs.filter(faq =>
        faq.question.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        // The outer Box no longer controls height or scrolling.
        <Box sx={{
            p: { xs: 2, md: 4 },
            maxWidth: "100%",
            mx: 'auto'
        }}>
            {/* --- HEADER AREA (Title and Search) --- */}
            <Typography
                variant="h5"
                component="h2"
                sx={{
                    textAlign: 'center',
                    mb: 3,
                    fontWeight: 600,
                    color: '#333',
                }}
            >
                Frequently Asked Questions
            </Typography>

            <Box sx={{ mb: 4, maxWidth: 'sm', mx: 'auto' }}>
                <TextField
                    fullWidth
                    placeholder="Search for topic"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: 'grey.500' }} />
                            </InputAdornment>
                        ),
                        sx: {
                            borderRadius: '100px',
                            backgroundColor: '#F9F9F9',
                            '& .MuiOutlinedInput-notchedOutline': {
                                border: '1.5px solid #DFDFDF',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                border: '1.5px solid #DFDFDF',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: '1.5px solid #DFDFDF',
                            },
                            '&.Mui-focused': {
                                boxShadow: 'none',
                            },
                        },
                    }}
                />
            </Box>

            {/* --- ACCORDION LIST --- */}
            {/* This Box no longer needs to be a scrollable container. */}
            <Box
                sx={{
                    width: '100%',
                }}
            >
                {filteredFaqs.map((faq, index) => (
                    <Accordion
                        key={index}
                        expanded={expanded === `panel${index}`}
                        onChange={handleAccordionChange(`panel${index}`)}
                        sx={{
                            boxShadow: 'none',
                            border: '1px solid #E0E0E0',
                            borderRadius: '12px !important',
                            mb: 1.5,
                            overflow: 'hidden',
                            '&:before': {
                                display: 'none',
                            },
                            '&.Mui-expanded': {
                                margin: '0 0 12px 0',
                            },
                            backgroundColor: "#F9F9F9"
                        }}
                    >
                        <AccordionSummary
                            expandIcon={
                                expanded === `panel${index}`
                                    ? <CloseIcon sx={{ color: '#1A0F91' }} />
                                    : <AddIcon sx={{ color: '#1A0F91' }} />
                            }
                            aria-controls={`panel${index}bh-content`}
                            id={`panel${index}bh-header`}
                        >
                            <Typography sx={{ fontWeight: expanded === `panel${index}` ? 600 : 500, color: '#333' }}>
                                {faq.question}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Typography sx={{ color: 'grey.700', lineHeight: 1.7 }}>
                                {faq.answer}
                            </Typography>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        </Box>
    );
};

export default FAQComponent;
